<template>
  <div class="kyc-verification-container">
    <div class="kyc-card">
      <div class="kyc-header">
        <h2>KYC Verification</h2>
        <p class="subtitle">Complete your KYC to proceed with investment services</p>
      </div>

      <!-- Progress Steps -->
      <div class="progress-steps">
        <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <span class="step-label">KRA Check</span>
        </div>
        <div class="step-line" :class="{ active: currentStep > 1 }"></div>
        <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
          <div class="step-number">2</div>
          <span class="step-label">Video KYC</span>
        </div>
        <div class="step-line" :class="{ active: currentStep > 2 }"></div>
        <div class="step" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
          <div class="step-number">3</div>
          <span class="step-label">Complete</span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="currentStep === 0 || isLoading" class="step-content loading-state">
        <div class="spinner"></div>
        <p>Checking KYC status...</p>
      </div>

      <!-- Step 1: KRA Status Check -->
      <div v-else-if="currentStep === 1" class="step-content">
        <KRAStatusCheck
          :panNumber="userPAN"
          @verified="handleKRAVerified"
          @not-verified="handleKRANotVerified"
        />
      </div>

      <!-- Step 2: Digio KYC -->
      <div v-else-if="currentStep === 2" class="step-content">
        <DigioKycStart
          :user="kycUserData"
          :debug="false"
          @kyc-completed="handleKYCCompleted"
          @kyc-failed="handleKYCFailed"
          @kyc-submitted="handleKYCSubmitted"
        />
      </div>

      <!-- Step 4: KYC Submitted - Under Review -->
      <div v-else-if="currentStep === 4" class="step-content pending-state">
        <div class="pending-icon">
          <i class="pi pi-clock"></i>
        </div>
        <h3>KYC Submitted for Verification</h3>
        <p>Your KYC has been submitted successfully and is currently under review. This process typically takes 24-48 hours.</p>

        <div class="info-box">
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value status-pending">{{ kycSubmittedData?.status || 'Submitted' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Submitted on:</span>
            <span class="info-value">{{ new Date().toLocaleDateString('en-IN') }}</span>
          </div>
        </div>

        <div class="pending-actions">
          <button @click="checkKYCStatus" :disabled="isCheckingStatus" class="primary-btn">
            {{ isCheckingStatus ? 'Checking...' : 'Check Verification Status' }}
          </button>
          <p class="pending-note">
            <i class="pi pi-info-circle"></i>
            You will be notified via email once your KYC is verified. You can also check the status anytime using the button above.
          </p>
        </div>
      </div>

      <!-- Step 3: Success -->
      <div v-else-if="currentStep === 3" class="step-content success-state">
        <div class="success-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <h3>KYC Verification Complete!</h3>
        <p>Your KYC has been successfully verified. You can now proceed to sign the agreement.</p>
        <button @click="proceedToAgreementSigning" class="primary-btn">
          Continue to Agreement Signing
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from '@/store/user-store';
import KRAStatusCheck from '@/components/dashboard/KRAStatusCheck.vue';
import DigioKycStart from '@/components/dashboard/DigioKycStart.vue';

export default {
  name: 'KYCVerification',
  components: {
    KRAStatusCheck,
    DigioKycStart
  },
  data() {
    return {
      currentStep: 0, // 0 = loading, 1 = KRA check, 2 = KYC, 3 = Complete, 4 = Pending/Submitted
      kraStatus: null,
      kycRequestId: null,
      kycCustomerIdentifier: null,
      isLoading: true,
      kycSubmittedData: null,
      isCheckingStatus: false
    };
  },
  computed: {
    store() {
      return useUserStore();
    },
    userPAN() {
      // Check all possible PAN field names from dashboard data
      const pan = this.store.dashboardData?.pan || '';
      console.log('userPAN computed:', pan, 'from dashboardData:', this.store.dashboardData);
      return pan;
    },
    kycUserData() {
      const dashboardData = this.store.dashboardData;
      return {
        name: `${dashboardData?.firstName || ''} ${dashboardData?.lastName || ''}`.trim(),
        phone: dashboardData?.phoneNumber || '',
        email: this.store.user?.email || '',
        id: dashboardData?.customerId || '',
        pan: this.userPAN
      };
    }
  },
  methods: {
    async checkExistingKYCStatus() {
      this.isLoading = true;
      console.log('=== KYC Verification Component Mounted ===');
      console.log('Dashboard Data:', this.store.dashboardData);
      console.log('User PAN:', this.userPAN);
      console.log('KYC Status from dashboard:', this.store.dashboardData?.kycStatus);

      try {
        const kycStatus = this.store.dashboardData?.kycStatus;

        if (kycStatus === 'Done') {
          // KYC already completed
          console.log('✅ KYC already completed - moving to step 3');
          this.currentStep = 3;
        } else {
          // KYC not done, start with KRA check
          console.log('🔄 KYC not completed - starting KRA check (step 1)');
          this.currentStep = 1;
        }
      } catch (error) {
        console.error('❌ Error checking KYC status:', error);
        // Start with KRA check on error
        console.log('⚠️ Defaulting to KRA check (step 1)');
        this.currentStep = 1;
      } finally {
        this.isLoading = false;
        console.log('Final currentStep:', this.currentStep);
      }
    },    handleKRAVerified(kraStatus) {
      this.kraStatus = kraStatus;
      // KRA verified - skip KYC and proceed to payment
      this.currentStep = 3;
      this.store.fireAlert('Success', 'Your PAN is already KRA verified. Proceeding to payment.');
    },

    handleKRANotVerified(kraStatus) {
      this.kraStatus = kraStatus;
      // KRA not verified - need to do KYC
      this.currentStep = 2;
      this.store.fireAlert('Info', 'KRA verification required. Please complete Video KYC.');
    },

    async handleKYCCompleted(kycData) {
      this.kycRequestId = kycData.RequestId;
      this.kycCustomerIdentifier = kycData.CustomerIdentifier;

      // Move to success step
      this.currentStep = 3;
      this.store.fireAlert('Success', 'KYC completed successfully!');
    },

    handleKYCFailed(error) {
      console.error('KYC failed:', error);
      this.store.fireAlert('Error', 'KYC verification failed. Please try again.');
    },

    handleKYCSubmitted(data) {
      console.log('KYC submitted:', data);
      this.kycSubmittedData = data;
      this.currentStep = 4; // Move to pending state
      this.store.fireAlert('Info', 'KYC submitted successfully. Verification under review.');
    },

    async checkKYCStatus() {
      this.isCheckingStatus = true;
      try {
        const response = await this.store.getKYCRequestStatus({
          pan: this.kycSubmittedData.pan,
          customerIdentifier: this.kycSubmittedData.customerIdentifier
        });

        console.log('KYC Status Check Response:', response);

        if (response.success && response.data) {
          const status = response.data.Status;

          if (status === 'Done' || status === 'Verified') {
            // KYC verified - proceed to payment
            this.currentStep = 3;
            await this.store.loadDashboardData(this.store.user.email);
            this.store.fireAlert('Success', 'KYC verified! You can now proceed to doc signing.');
          } else if (status === 'Submitted' || status === 'Pending') {
            this.store.fireAlert('Info', 'KYC still under review. Please check back later.');
          } else {
            this.store.fireAlert('Info', `Current status: ${status}`);
          }
        } else {
          this.store.fireAlert('Warning', 'Unable to fetch KYC status. Please try again.');
        }
      } catch (error) {
        console.error('Error checking KYC status:', error);
        this.store.fireAlert('Error', 'Error checking status. Please try again later.');
      } finally {
        this.isCheckingStatus = false;
      }
    },

    async proceedToAgreementSigning() {
      try {
        // Reload dashboard data to ensure latest status
        await this.store.loadDashboardData(this.store.user.email);

        // Navigate to agreement signing - let loadDashboardData handle the correct navigation
        // It will check docSigningStatus and navigate appropriately
        this.store.setTabView('AgreementSigning');
      } catch (error) {
        console.error('Error proceeding to agreement signing:', error);
        this.store.fireAlert('Error', 'Failed to proceed. Please try again.');
      }
    }
  },
  async mounted() {
    document.title = 'KYC Verification - AssetNivesh';

    console.log('KYCVerification mounted - checking PAN availability');

    // Auto-start KRA check if PAN is available
    if (!this.userPAN) {
      console.error('❌ No PAN found in dashboard data');
      this.store.fireAlert('Error', 'PAN number not found. Please complete your profile first.');
      this.store.setTabView('ManageProfile');
      return;
    }

    console.log('✅ PAN found:', this.userPAN);

    // Check existing KYC status
    await this.checkExistingKYCStatus();
  }
};
</script>

<style scoped>
.kyc-verification-container {
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
}

.kyc-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 32px;
}

.kyc-header {
  text-align: center;
  margin-bottom: 32px;
}

.kyc-header h2 {
  color: #2c3e50;
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
}

.subtitle {
  color: #666;
  margin: 0;
  font-size: 16px;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 48px;
  padding: 0 20px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e0e0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  transition: all 0.3s;
}

.step.active .step-number {
  background: #AB3498;
  color: #fff;
}

.step.completed .step-number {
  background: #28a745;
  color: #fff;
}

.step-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  white-space: nowrap;
}

.step.active .step-label {
  color: #AB3498;
  font-weight: 600;
}

.step-line {
  flex: 1;
  height: 2px;
  background: #e0e0e0;
  margin: 0 16px;
  transition: all 0.3s;
}

.step-line.active {
  background: #28a745;
}

/* Step Content */
.step-content {
  min-height: 300px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(171, 52, 152, 0.2);
  border-top: 4px solid #AB3498;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p {
  color: #666;
  font-size: 16px;
  margin: 0;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 40px 20px;
}

.success-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #d4edda;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.success-icon i {
  font-size: 48px;
  color: #28a745;
  animation: success-pop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes success-pop {
  0% { transform: scale(0); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.success-state h3 {
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 600;
}

.success-state p {
  color: #666;
  margin: 0 0 32px 0;
  font-size: 16px;
  line-height: 1.6;
}

/* Pending State */
.pending-state {
  text-align: center;
  padding: 40px 20px;
}

.pending-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fff3cd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.pending-icon i {
  font-size: 48px;
  color: #856404;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.pending-state h3 {
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 600;
}

.pending-state p {
  color: #666;
  margin: 0 0 24px 0;
  font-size: 16px;
  line-height: 1.6;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.info-box {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin: 0 auto 32px;
  max-width: 400px;
  text-align: left;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #666;
  font-weight: 500;
}

.info-value {
  color: #2c3e50;
  font-weight: 600;
}

.status-pending {
  color: #856404;
}

.pending-actions {
  max-width: 400px;
  margin: 0 auto;
}

.pending-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #e7f3ff;
  border-left: 4px solid #2196f3;
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;
  text-align: left;
  color: #004085;
  font-size: 14px;
  line-height: 1.5;
}

.pending-note i {
  color: #2196f3;
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

.primary-btn {
  padding: 14px 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: #AB3498;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s;
}

.primary-btn:hover {
  background: #8e2a7d;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(171, 52, 152, 0.3);
}

/* Responsive */
@media (max-width: 768px) {
  .kyc-verification-container {
    padding: 16px;
  }

  .kyc-card {
    padding: 24px;
  }

  .kyc-header h2 {
    font-size: 24px;
  }

  .progress-steps {
    padding: 0;
  }

  .step-number {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .step-label {
    font-size: 12px;
  }

  .step-line {
    margin: 0 8px;
  }
}
</style>
